import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { Search } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { toast_success } from '@/libs/toast-wrappers';
import { formatNpiProviderName } from '@/utils/strings';
import { US_STATES_ARRAY } from '@/libs/us-states';
import { NpiProvider } from '@/api/npi-preferred';
import { Site } from '@/api/sites';
import { useGetNpiProvidersByFilter } from '@/hooks/api/use-get-npi-providers-by-filter';
import { usePagination } from '@/hooks/api/use-pagination';
import { useSavePreferredProvider } from '@/hooks/api/use-save-npi-preferred-provider';

import { Hr } from '@/components/ui/divider';
import { InputField } from '@/components/form/input-field';
import { SelectField } from '@/components/form/select-field';
import { FetchStates } from '@/components/fetch-states';
import { Table } from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';

const createSchema = (t: TFunction) =>
  z
    .object({
      city: z.string().trim(),
      state: z.string().trim().min(1, t('input-required')),
      postal_code: z.string().trim(),
    })
    // cross-field rule: at least one of city or postal_code must be provided
    .superRefine((data, ctx) => {
      if (!data.city && !data.postal_code) {
        // attach error to city field, telling the user postal_code is the alternative
        ctx.addIssue({
          code: 'custom' as const,
          path: ['city'],
          message: t('input-required-if-not').replace('{field}', t('postal-code-label')),
        });
        // attach error to postal_code field, telling the user city is the alternative
        ctx.addIssue({
          code: 'custom' as const,
          path: ['postal_code'],
          message: t('input-required-if-not').replace('{field}', t('city-label')),
        });
      }
    });

type InputTypes = z.infer<ReturnType<typeof createSchema>>;

const defaultValues: InputTypes = {
  city: '',
  state: '',
  postal_code: '',
};

type SiteNpiSearchProps = {
  onConfirmNewPreferred?: () => void;
  currentPreferred?: NpiProvider[];
  disabled?: boolean;
};

const PAGE_LIMIT = 15;

export function SiteNpiSearch(props: SiteNpiSearchProps) {
  const { onConfirmNewPreferred, currentPreferred, disabled } = props;

  const location: { state?: { site?: Site } } = useLocation();
  const { site } = location.state ?? {};

  const { t } = useTranslation();

  const schema = useMemo(() => createSchema(t), [t]);

  const { pagination, moveToNextPage, moveToPreviousPage, resetPagination } =
    usePagination(PAGE_LIMIT);
  const { request, data, loading } = useGetNpiProvidersByFilter();
  const { request: saveProvider, loading: saveLoading } = useSavePreferredProvider();

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<InputTypes>({ defaultValues, resolver: zodResolver(schema) });

  const onSubmitSearchNPI: SubmitHandler<InputTypes> = (data) => {
    if (!site) return;

    resetPagination();

    void request({
      state: data.state,
      city: data.city,
      postal_code: data.postal_code,
      limit: PAGE_LIMIT,
      skip: 0,
    });
  };

  const formatProviderName = useCallback(formatNpiProviderName, []);

  const onAddPreferred = useCallback(
    async (preferred: NpiProvider) => {
      if (!site) return;
      await saveProvider({ site_id: site.site_id, npi_number: preferred.number });
      toast_success(t('npi-preferred-added'));
      onConfirmNewPreferred?.();
    },
    [site, saveProvider]
  );

  const headers = useMemo(
    () => [
      { key: 'number', label: t('npi-number'), type: 'value' as const },
      { key: 'enumeration_type', label: t('npi-type'), type: 'value' as const },
      { key: 'name', label: t('npi-name'), format: formatProviderName },
      {
        key: 'actions',
        type: 'actions' as const,
        add: (_preferred: unknown) => {
          const preferred = _preferred as NpiProvider;
          void onAddPreferred(preferred);
        },
        disabled: (_preferred: unknown) => {
          const preferred = _preferred as NpiProvider;
          return Boolean(currentPreferred?.some((p) => p.number === preferred.number));
        },
      },
    ],
    [currentPreferred?.length]
  );

  const handleMoveToNextPage = useCallback(() => {
    const page = moveToNextPage();
    void request({
      state: getValues('state'),
      city: getValues('city'),
      postal_code: getValues('postal_code'),
      limit: pagination.limit,
      skip: page,
    });
  }, [moveToNextPage, getValues, pagination.limit, request]);

  const handleMoveToPreviousPage = useCallback(() => {
    const page = moveToPreviousPage();
    void request({
      state: getValues('state'),
      city: getValues('city'),
      postal_code: getValues('postal_code'),
      limit: pagination.limit,
      skip: page,
    });
  }, [moveToPreviousPage, getValues, pagination.limit, request]);

  return (
    <form className="mt-16" onSubmit={handleSubmit(onSubmitSearchNPI)}>
      <Hr />
      <div className="flex items-center justify-between mb-4">
        <h3 className="input-label">{t('site-search-npi')}</h3>
      </div>
      <div className="max-w-2xl mb-8 flex flex-wrap items-end gap-4">
        <div className="flex-1">
          <SelectField
            disabled={loading || saveLoading}
            {...register('state')}
            label={t('state-label')}
            options={US_STATES_ARRAY ?? []}
            errorMessage={errors.state?.message}
          />
        </div>
        <div className="flex-1">
          <InputField
            disabled={loading || saveLoading}
            {...register('city')}
            label={t('city-label')}
            errorMessage={errors.city?.message}
          />
        </div>
        <div className="flex-1">
          <InputField
            disabled={loading || saveLoading}
            {...register('postal_code')}
            label={t('postal-code-label')}
            type="number"
            errorMessage={errors.postal_code?.message}
          />
        </div>
        <div className="shrink">
          <Button type="submit" disabled={loading || saveLoading}>
            <Search />
          </Button>
        </div>
      </div>
      <FetchStates loading={loading}>
        {data?.length ? (
          <>
            <div className="w-full relative overflow-auto">
              <Table
                headers={headers}
                data={data ?? []}
                disabled={saveLoading || disabled}
                cellClassName="max-w-[300px]"
              />
            </div>
            <Pagination
              onMoveForward={handleMoveToNextPage}
              onMoveBackward={handleMoveToPreviousPage}
              isBackDisabled={pagination.skip === 0}
            />
          </>
        ) : null}
      </FetchStates>
    </form>
  );
}
